/**
 * Central query-key registry.
 *
 * Every TanStack Query key in the admin SPA is defined here. Hooks
 * call `queryKeys.tenants.list(filter)` rather than assembling the
 * array inline. Benefits:
 *
 *   1. Single place to see every cache key the app uses — no hunting.
 *   2. Cache invalidations (mutation onSuccess) can use prefix keys:
 *      `qc.invalidateQueries({ queryKey: queryKeys.tenants.all })`
 *      invalidates every tenant-related query in one line.
 *   3. Keys are typed with `as const`, so TanStack Query's internal
 *      key comparison is reliable and TS autocomplete works at the
 *      call site.
 *
 * Convention: every feature gets a sub-object with at minimum an
 *   `all` (broad prefix),
 *   `list(filter)` / `detail(id)` / `... (id, nested)` members.
 *
 * Keep this file framework-agnostic — no imports from TanStack Query
 * itself, just data.
 */

export type ReadonlyKey = readonly unknown[];

export const queryKeys = {
	productAreas: {
		all: (tenantId: string) => ["productAreas", tenantId] as const,
		list: (tenantId: string) => ["productAreas", tenantId, "list"] as const,
		census: (tenantId: string) => ["productAreas", tenantId, "census"] as const,
		setupPreview: (tenantId: string, area: string) =>
			["productAreas", tenantId, "setupPreview", area] as const,
		applicationWorkspace: (tenantId: string, applicationId: string) =>
			["productAreas", tenantId, "applicationWorkspace", applicationId] as const,
		automatedOnboarding: (tenantId: string) =>
			["productAreas", tenantId, "automatedOnboarding"] as const,
		applicationCreation: (tenantId: string) =>
			["productAreas", tenantId, "applicationCreation"] as const,
	},
  // ── Cross-feature / infra ──────────────────────────────────────────────
  session: ["session"] as const,
  adminMe: ["admin", "me"] as const,
  /** The signed-in admin's OWN durability posture. GET /me/account-health — not a platform surface. */
  accountHealth: ["me", "account-health"] as const,

  // ── Platform dashboard ─────────────────────────────────────────────────
  platformOverview: ["platform", "overview"] as const,
  /** Static-ish RBAC catalog (roles + permissions). Cached for the session. */
  rbacCatalog: ["rbac", "catalog"] as const,

  // ── Directory sync ─────────────────────────────────────────────────────
  directory: {
    all: (tenantSlug: string) => ["directory", tenantSlug] as const,
    overview: (tenantSlug: string) => ["directory", tenantSlug, "overview"] as const,

    // AD inbound directories (Renegadir-Connect). Keyed by tenant UUID —
    // matches every other UUID-keyed feature (agents, applications).
    adInbound: {
      all: (tenantID: string) => ["directory", tenantID, "ad-inbound"] as const,
      list: (tenantID: string, offset = 0) =>
        ["directory", tenantID, "ad-inbound", "list", offset] as const,
      detail: (tenantID: string, directoryID: string) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID] as const,
      // A Workday directory's own view: its report, credential and pulls.
      workday: (tenantID: string, directoryID: string) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID, "workday"] as const,
      ouList: (tenantID: string, directoryID: string) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID, "ou-list"] as const,
      // `detail` is a strict array-prefix of `users`/`groups`, so
      // invalidating `detail(tenantID, directoryID)` sweeps every
      // paginated/filtered users+groups cache entry for this directory
      // too — same convention as groups.detail/members above.
      users: (tenantID: string, directoryID: string, filter: Readonly<Record<string, unknown>> = {}) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID, "users", filter] as const,
      groups: (tenantID: string, directoryID: string, filter: Readonly<Record<string, unknown>> = {}) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID, "groups", filter] as const,
      // Records that failed before resolving to a user or group. Same
      // `detail`-prefix convention, so a directory mutation sweeps these too.
      rejects: (tenantID: string, directoryID: string, filter: Readonly<Record<string, unknown>> = {}) =>
        ["directory", tenantID, "ad-inbound", "detail", directoryID, "rejects", filter] as const,
    },
  },

  // ── Getting started ────────────────────────────────────────────────────
  gettingStarted: (tenantSlug: string) =>
    ["getting-started", tenantSlug] as const,

  // ── Onboarding-health dashboard (P0e) ──────────────────────────────────
  // Per-tenant 24h funnel + top failure reasons + OTP delivery
  // health. Backed by GET /api/v1/tenants/{tenant_id}/onboarding-health.
  onboardingHealth: (tenantSlug: string) =>
    ["onboarding-health", tenantSlug] as const,

  // ── DD-NEXT-7 outcome-telemetry auto-tune ──────────────────────────────
  // Per-tenant kill switch + per-(device_class, playbook) verdict
  // cells. Backed by GET /api/v1/tenants/{tenant_id}/onboarding-autotune.
  onboardingAutotune: (tenantSlug: string) =>
    ["onboarding-autotune", tenantSlug] as const,

  // ── Compliance ─────────────────────────────────────────────────────────
  compliance: {
    all: (tenantSlug: string) => ["compliance", tenantSlug] as const,
    reports: (tenantSlug: string) => ["compliance", tenantSlug, "reports"] as const,
    // A finished run's document is immutable, so the key never needs a
    // version: the bytes behind it cannot change.
    reportDocument: (tenantSlug: string, reportID: string) =>
      ["compliance", tenantSlug, "reports", reportID, "document"] as const,
    mandateRuleSets: (tenantSlug: string) =>
      ["compliance", tenantSlug, "mandate-rule-sets"] as const,
  },

  // ── Devices ────────────────────────────────────────────────────────────
  devices: {
    all: (tenantSlug: string) => ["devices", tenantSlug] as const,
    list: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["devices", tenantSlug, "list", filter] as const,
  },

  // ── Groups ─────────────────────────────────────────────────────────────
  groups: {
    all: (tenantSlug: string) => ["groups", tenantSlug] as const,
    list: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["groups", tenantSlug, "list", filter] as const,
    detail: (tenantSlug: string, groupId: string) =>
      ["groups", tenantSlug, "detail", groupId] as const,
    members: (tenantSlug: string, groupId: string) =>
      ["groups", tenantSlug, "detail", groupId, "members"] as const,
  },

  // ── Tenants ────────────────────────────────────────────────────────────
  tenants: {
    all: ["tenants"] as const,
    list: (filter: Readonly<Record<string, unknown>> = {}) =>
      ["tenants", "list", filter] as const,
    detail: (slug: string) => ["tenants", "detail", slug] as const,
    settings: (slug: string) => ["tenants", "detail", slug, "settings"] as const,
    stats: (slug: string) => ["tenants", "detail", slug, "stats"] as const,
    /** Per-tenant overview rollup — keyed by tenant_id (UUID), not slug,
     *  since the backend endpoint accepts the UUID directly. */
    overview: (tenantID: string) => ["tenants", "overview", tenantID] as const,
    /** The one inbox on Tenant Home. Keyed by tenant_id for the same reason
     *  as the overview, and kept SEPARATE from it: the inbox polls every 30s
     *  while the overview does not, so sharing a key would either over-fetch
     *  the rollup or under-fetch the decisions. */
    inbox: (tenantID: string) => ["tenants", "inbox", tenantID] as const,
  },

  // ── Users ──────────────────────────────────────────────────────────────
  users: {
    // Tenant-scoped root: every child key includes tenantSlug at
    // index 1, so `invalidateQueries({ queryKey: users.all(slug) })`
    // invalidates exactly this tenant's user data and doesn't touch
    // another tenant's cache when an admin is hopping between
    // tenants in the same session.
    all: (tenantSlug: string) => ["users", tenantSlug] as const,
    list: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["users", tenantSlug, "list", filter] as const,
    // Batch id→label resolution for pickers/tables. Keyed by the SORTED id set so the same selection
    // dedupes/caches regardless of order (and a superset is a distinct, correct cache entry).
    refs: (tenantSlug: string, ids: readonly string[]) =>
      ["users", tenantSlug, "refs", [...ids].sort()] as const,
    detail: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId] as const,
    passkeys: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "passkeys"] as const,
    deviceIdentities: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "device-identities"] as const,
    sessions: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "sessions"] as const,
    activity: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "activity"] as const,
    devices: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "devices"] as const,
    // Registered mobile-authenticator installs (push targets). Nested under
    // detail/<userId> so a user-level invalidation sweeps them too.
    pushTokens: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "push-tokens"] as const,
    // §B Phase E — admin-issued Temporary Access Passes for a user.
    // Nested under detail/<userId> so a user-level invalidation
    // sweeps TAP caches alongside passkey / session caches.
    temporaryAccessPasses: (tenantSlug: string, userId: string) =>
      ["users", tenantSlug, "detail", userId, "temporary-access-passes"] as const,
  },

  // ── Non-human identity census ──────────────────────────────────────────
  // The complete roster spanning agents, automation accounts, SCIM connector
  // tokens, auditor tokens and AD-inbound directories. Its own key namespace
  // rather than a child of `agents`, because its freshness must follow ALL FIVE
  // sources — nesting it under agents would tie the roster to one of them.
  //
  // What keeps it fresh is wireCensusInvalidation (lib/query-client.ts), which
  // invalidates this key whenever any source key is invalidated. That is stated
  // here because this comment used to assert the mutating surfaces invalidated
  // the census themselves, and none of them did.
  nhiCensus: {
    all: ["nhi-census"] as const,
    list: (tenantID: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["nhi-census", "list", tenantID, filter] as const,
  },

  // Two census sources whose keys were written inline at every call site.
  //
  // They are here because they are the two that DRIFTED: the first version of
  // wireCensusInvalidation guessed both from folder names, and both missed — the
  // auditor-token one on the very example its docstring cites. The invalidation
  // test then "pinned" them by restating the same literals, which pins a guess
  // against a copy of itself and notices nothing.
  //
  // Their shapes are inherited (`scim`, `compliance-v2`) rather than renamed to
  // match this file's conventions, because renaming a key is a cache-busting
  // change for every open console and buys nothing here. One definition is the
  // whole point; a tidy definition is not.
  scimTokens: {
    all: (tenantID: string) => ["scim", "tokens", tenantID] as const,
    allTokens: (tenantID: string) => ["scim", "tokens-all", tenantID] as const,
  },
  // Identity sources: the Sources tab's rows, keyed by tenant UUID.
  identitySources: {
    all: (tenantID: string) => ["identity-sources", tenantID] as const,
  },
  // Frontline's configuration (offering switches, methods, session policy).
  // Here rather than in the feature so the product-area switch, which clears
  // the offerings server-side, can drop it.
  frontline: {
    all: (tenantID: string) => ["admin", "frontline", tenantID] as const,
  },
  // ── Organizations (CIAM Slice 10) ──────────────────────────────────────
  organizations: {
    all: (tenantID: string) => ["organizations", tenantID] as const,
    list: (tenantID: string, search: Readonly<Record<string, unknown>> = {}) =>
      ["organizations", tenantID, "list", search] as const,
    detail: (tenantID: string, orgID: string) =>
      ["organizations", tenantID, "detail", orgID] as const,
    // Who holds the organization's confined console (the Delegated access tab).
    grants: (tenantID: string, orgID: string, includeRevoked: boolean) =>
      ["organizations", tenantID, "detail", orgID, "grants", includeRevoked] as const,
    // Every organization grant in the tenant (Settings → Admins → Delegated).
    allGrants: (tenantID: string, includeRevoked: boolean) =>
      ["organizations", tenantID, "grants", includeRevoked] as const,
  },
  auditorTokens: {
    all: (tenantID: string) => ["compliance-v2", "auditor-tokens", tenantID] as const,
  },

  // The parties holding copies of this deployment's transparency-log
  // tree heads.
  transparencyWitnesses: {
    all: (tenantID: string) => ["compliance-v2", "transparency-witnesses", tenantID] as const,
  },

  // ── Agents (delegated agent identity, Phase 1) ─────────────────────────
  // tenantID-keyed (the param is named tenantID but, as with applications,
  // receives the resolved tenant UUID from the route context).
  agents: {
    all: ["agents"] as const,
    list: (tenantID: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["agents", "list", tenantID, filter] as const,
    detail: (tenantID: string, agentId: string) =>
      ["agents", "detail", tenantID, agentId] as const,
    // First-run pairing provenance (fingerprint + who vouched). Nested under detail so an agent-level
    // invalidation sweeps it alongside the record.
    enrollment: (tenantID: string, agentId: string) =>
      ["agents", "detail", tenantID, agentId, "enrollment"] as const,
    standingDelegations: (tenantID: string) =>
      ["agents", "standing-delegations", tenantID] as const,
    standingBudgetBreakdown: (tenantID: string, standingID: string) =>
      ["agents", "standing-delegations", tenantID, standingID, "budget-breakdown"] as const,
    standingCrewView: (tenantID: string, standingID: string) =>
      ["agents", "standing-delegations", tenantID, standingID, "crew"] as const,
    lineage: (tenantID: string, agentId: string) =>
      ["agents", "detail", tenantID, agentId, "lineage"] as const,
    // Blast-radius preview for the stop/pause dialogs. Nested under detail so an agent-level
    // invalidation sweeps it (the preview is stale the moment the stop it previews has run).
    stopPreview: (tenantID: string, agentId: string) =>
      ["agents", "detail", tenantID, agentId, "stop-preview"] as const,
    // Cross-identity carried-spend rollup over an agent's re-provision lineage chain
    // (budget-reset gaming remediation, Item 3). Nested under detail so an agent-level
    // invalidation sweeps it alongside the record + activity caches.
    chainRollup: (tenantID: string, agentId: string) =>
      ["agents", "detail", tenantID, agentId, "budget-chain-rollup"] as const,
    evidence: (tenantID: string, jti: string) =>
      ["agents", "evidence", tenantID, jti] as const,
    chainVerify: (tenantID: string) =>
      ["agents", "evidence", "chain-verify", tenantID] as const,
    grants: (tenantID: string, includeAll: boolean, offset = 0) =>
      ["agents", "grants", tenantID, includeAll, offset] as const,
    protectedResources: (tenantID: string) =>
      ["agents", "protected-resources", tenantID] as const,
    serverRoster: (tenantID: string) => ["agents", "server-roster", tenantID] as const,
    resourceCustody: (tenantID: string, resource: string) =>
      ["agents", "protected-resources", tenantID, "custody", resource] as const,
    resourceOperations: (tenantID: string, resource: string) =>
      ["agents", "protected-resources", tenantID, "operations", resource] as const,
    approvalHistory: (tenantID: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["agents", "approval-history", tenantID, filter] as const,
    // Item 3 budget-reset anomaly ledger (fleet dashboard). The list is filter/paginated; the
    // actionable-count badge is its own key so a filtered view can't invalidate/confuse the badge.
    budgetResetAnomalies: (tenantID: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["agents", "budget-reset-anomalies", tenantID, filter] as const,
    budgetResetActionableCount: (tenantID: string) =>
      ["agents", "budget-reset-anomalies", "actionable-count", tenantID] as const,
  },

  workloadIssuers: {
    all: ["workload-issuers"] as const,
    list: (tenantID: string) => ["workload-issuers", "list", tenantID] as const,
  },

  // ── Trusted enrollment issuers ─────
  // Platform-admin only; keyed by tenant UUID.
  trustedEnrollmentIssuers: {
    all: ["trusted-enrollment-issuers"] as const,
    list: (tenantID: string) =>
      ["trusted-enrollment-issuers", "list", tenantID] as const,
  },

  // ── Applications ───────────────────────────────────────────────────────
  applications: {
    all: ["applications"] as const,
    list: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["applications", "list", tenantSlug, filter] as const,
    detail: (tenantSlug: string, appId: string) =>
      ["applications", "detail", tenantSlug, appId] as const,
    usage: (tenantSlug: string, appId: string) =>
      ["applications", "detail", tenantSlug, appId, "usage"] as const,
    /** The estate view: what every SAML application identifies people with. */
    nameIDReport: (tenantSlug: string) =>
      ["applications", "name-identifiers", tenantSlug] as const,
    /** Cross-app access roster (granted groups + users per app).
     *  Drives the admin "Policies & access" list view. Keyed by
     *  tenant UUID plus the pagination window so a future UI
     *  paginator gets isolated cache entries per page rather than
     *  clobbering the default-page cache. */
    accessRoster: (
      tenantID: string,
      search: Readonly<Record<string, unknown>> = {},
    ) => ["applications", "accessRoster", tenantID, search] as const,
    /** Prefix used for broad invalidations after a grant mutation
     *  so every paginated roster cache entry refetches. Stops
     *  short of the `search` slot so TanStack's default prefix
     *  match picks up every page. */
    accessRosterAll: (tenantID: string) =>
      ["applications", "accessRoster", tenantID] as const,
    /** Per-app access set (granted user + group UUIDs). Returned by
     *  GET /applications/{id}/access and by every /access/users |
     *  /access/groups PUT so the editor form stays in sync without
     *  a follow-up read. */
    access: (tenantID: string, appId: string) =>
      ["applications", "access", tenantID, appId] as const,
    configurationCheck: (tenantID: string, appId: string) =>
      ["applications", "configurationCheck", tenantID, appId] as const,
    pilotVerification: (tenantID: string, appId: string) =>
      ["applications", "pilotVerification", tenantID, appId] as const,
    /** Live OIDC discovery document for the tenant, fetched from
     *  `https://{tenantHost}/.well-known/openid-configuration`.
     *  Keyed by host (not tenant UUID) because that is the exact
     *  origin the document is served from, and the same host shape
     *  is what the setup panel displays to the operator. */
    oidcDiscovery: (tenantHost: string) =>
      ["applications", "oidcDiscovery", tenantHost] as const,
    /** SAML IdP signing cert for a specific application.
     *  Keyed by tenant UUID + app UUID so a rotation
     *  invalidation touches only the affected app's cache. */
    samlCert: (tenantID: string, appId: string) =>
      ["applications", "samlCert", tenantID, appId] as const,
  },

  // ── Passkeys ───────────────────────────────────────────────────────────
  passkeys: {
    // Tenant-scoped — list queries carry the slug at index 1 so a
    // prefix invalidation via all(slug) targets exactly this
    // tenant's passkey caches and never spills across tenants.
    all: (tenantSlug: string) => ["passkeys", tenantSlug] as const,
    list: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["passkeys", tenantSlug, "list", filter] as const,
  },

  // ── Audit ──────────────────────────────────────────────────────────────
  audit: {
    all: ["audit"] as const,
    tenantEvents: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["audit", "tenant", tenantSlug, filter] as const,
    windowsAuthentications: (tenantSlug: string, filter: Readonly<Record<string, unknown>> = {}) =>
      ["audit", "tenant", tenantSlug, "windows-authentication", filter] as const,
    platformEvents: (filter: Readonly<Record<string, unknown>> = {}) =>
      ["audit", "platform", filter] as const,
  },

  // ── Security (conditional access policies, named locations) ────────────
  policies: {
    all: (tenantSlug: string) => ["policies", tenantSlug] as const,
    list: (tenantSlug: string) => ["policies", tenantSlug, "list"] as const,
    detail: (tenantSlug: string, policyId: string) =>
      ["policies", tenantSlug, "detail", policyId] as const,
  },
  namedLocations: {
    all: (tenantSlug: string) => ["namedLocations", tenantSlug] as const,
    list: (tenantSlug: string) => ["namedLocations", tenantSlug, "list"] as const,
    detail: (tenantSlug: string, locationId: string) =>
      ["namedLocations", tenantSlug, "detail", locationId] as const,
  },

  // ── Webhooks ───────────────────────────────────────────────────────────
  notifications: {
    all: (tenantID: string) => ["notifications", tenantID] as const,
    subscriptions: (tenantID: string) => ["notifications", tenantID, "subscriptions"] as const,
    eventTypes: (tenantID: string) => ["notifications", tenantID, "event-types"] as const,
  },

  webhooks: {
    all: (tenantSlug: string) => ["webhooks", tenantSlug] as const,
    list: (tenantSlug: string) => ["webhooks", tenantSlug, "list"] as const,
    detail: (tenantSlug: string, webhookId: string) =>
      ["webhooks", tenantSlug, "detail", webhookId] as const,
    deliveries: (tenantSlug: string, webhookId: string) =>
      ["webhooks", tenantSlug, "detail", webhookId, "deliveries"] as const,
  },

  logonCertificates: {
    all: (tenantId: string) => ["logonCertificates", tenantId] as const,
    readiness: (tenantId: string) => ["logonCertificates", tenantId, "readiness"] as const,
    revocations: (tenantId: string) => ["logonCertificates", tenantId, "revocations"] as const,
    profile: (tenantId: string) => ["logonCertificates", tenantId, "profile"] as const,
    // Outstanding CSR-out signing requests. Tenant-scoped rather than per-forest:
    // an operator returning weeks later with a signed certificate is looking for "what is outstanding
    // here", and a forest key would need the answer before it could ask the question.
    pendingCSRs: (tenantId: string) => ["logonCertificates", tenantId, "pendingCSRs"] as const,
    // Keyed by FOREST: two forests' packets must never share a cache entry, because the mistake this whole
    // area guards against is publishing one forest's authority into another's NTAuth.
    trust: (tenantId: string, forestDNS: string) =>
      ["logonCertificates", tenantId, "trust", forestDNS] as const,
  },

  // ── EdgeDeployments (the on-premises enforcement tier) ────────────────────────
  // Scoped by tenant UUID because an edge belongs to exactly one tenant
  // (nhi.md) and the roster is read on the tenant-host tree.
  edgeDeployments: {
    all: (tenantId: string) => ["edge-deployments", tenantId] as const,
    list: (tenantId: string) => ["edge-deployments", tenantId, "list"] as const,
    evidenceKeys: (tenantId: string, edgeId: string) =>
      ["edge-deployments", tenantId, "evidenceKeys", edgeId] as const,
  },

  // ── Workstations (the Windows credential provider's device plane) ──────
  // Scoped by tenant UUID like edge deployments above, and for the same reason: a
  // workstation belongs to exactly one tenant, and the roster is read on the
  // tenant-host tree where the tenant comes from the session and the host.
  workstations: {
    all: (tenantId: string) => ["workstations", tenantId] as const,
    list: (tenantId: string) => ["workstations", tenantId, "list"] as const,
    tokens: (tenantId: string) => ["workstations", tenantId, "enrollmentTokens"] as const,
    policy: (tenantId: string) => ["workstations", tenantId, "policy"] as const,
    // Keyed by DEVICE: "effective policy for this machine" is a per-machine
    // answer, and two machines sharing one cache entry is the single mistake
    // that would make a shielded row report another machine's lockdown state.
    effective: (tenantId: string, deviceId: string) =>
      ["workstations", tenantId, "effective", deviceId] as const,
    impact: (tenantId: string) => ["workstations", tenantId, "policyImpact"] as const,
    // The correlation GUID is IN the key because it is the query — the trail
    // for one attempt and the unfiltered trail are different answers and must
    // never share an entry.
    events: (tenantId: string, correlationId: string, deviceId: string) =>
      ["workstations", tenantId, "events", correlationId, deviceId] as const,
  },

  // ── Admin users (platform + per-tenant) / API keys ─────────────────────
  // Scoped by `scope`: the sentinel 'platform' for platform admins
  // (admin_users.tenant_id IS NULL), or the tenant UUID for a
  // tenant's admins. Keeping the scope in the key prevents cross-tenant
  // cache bleed when an admin hops between tenants mid-session — same
  // convention as automationAccounts below.
  adminUsers: {
    all: (scope: string) => ["adminUsers", scope] as const,
    list: (scope: string) => ["adminUsers", scope, "list"] as const,
  },
  apiKeys: {
    all: ["apiKeys"] as const,
    list: ["apiKeys", "list"] as const,
  },

  // ── Private access (platform settings) ──────────────────────────────
  privateAccess: {
    certificateRecordCheck: ["privateAccess", "certificateRecordCheck"] as const,
  },

  // ── OIDC Dynamic Client Registration (RFC 7591) ───────────────────────
  // Tenant-scoped: a tenant's IATs and policy are isolated by Host.
  oauthDynreg: {
    all: (tenantId: string) => ["oauthDynreg", tenantId] as const,
    iats: (tenantId: string, includeRevoked: boolean) =>
      ["oauthDynreg", tenantId, "iats", { includeRevoked }] as const,
    policy: (tenantId: string) =>
      ["oauthDynreg", tenantId, "policy"] as const,
    reviews: (tenantId: string, state: string) =>
      ["oauthDynreg", tenantId, "reviews", { state }] as const,
  },

  // ── Cascade-order policy (Phase B.3 §4.6) ─────────────────────────────
  cascadePolicy: {
    all: (tenantId: string) => ["cascadePolicy", tenantId] as const,
    policy: (tenantId: string) => ["cascadePolicy", tenantId, "policy"] as const,
  },

  // ── Automation accounts ────────────────────────────────────────────────
  // Scoped by `scope`: the sentinel 'platform' for the platform
  // surface, or the tenant UUID for the tenant surface. Keeping the
  // scope in the key prevents cross-tenant cache bleed when an admin
  // hops between tenants mid-session.
  automationAccounts: {
    all: (scope: string) => ["automationAccounts", scope] as const,
    list: (scope: string) => ["automationAccounts", scope, "list"] as const,
    detail: (scope: string, id: string) =>
      ["automationAccounts", scope, "detail", id] as const,
  },
  authenticatorAllowlist: {
    all: (tenantId: string) =>
      ["authenticatorAllowlist", tenantId] as const,
    list: (tenantId: string) =>
      ["authenticatorAllowlist", tenantId, "list"] as const,
  },

  // ── Admin recovery queue (P0.7 §4.7 cascade tier 7) ─────────────────────
  adminRecovery: {
    all: (tenantId: string) => ["adminRecovery", tenantId] as const,
    pending: (tenantId: string) => ["adminRecovery", tenantId, "pending"] as const,
    decided: (tenantId: string) => ["adminRecovery", tenantId, "decided"] as const,
  },

  // ── Passkey intelligence ───────────────────────────────────────────────
  passkeyIntelligence: {
    overview: (tenantSlug: string) =>
      ["passkeyIntelligence", tenantSlug, "overview"] as const,
    credentialHealth: (tenantSlug: string) =>
      ["passkeyIntelligence", tenantSlug, "credentialHealth"] as const,
    migration: (tenantSlug: string) =>
      ["passkeyIntelligence", tenantSlug, "migration"] as const,
  },
} as const;
